<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import KanbanReportWidget from 'kanban/components/reports/KanbanReportWidget.vue';
import { stepColor } from 'kanban/constants/reports';
import {
  formatNumber,
  formatCurrencyValue,
  formatDuration,
} from 'kanban/helper/reportFormatters';

const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  locale: {
    type: String,
    default: 'en',
  },
});

const emit = defineEmits(['retry', 'update:page']);

const { t } = useI18n();

const currency = computed(() => props.data?.currency);
const stepsSummary = computed(() => props.data?.steps_summary || []);
const items = computed(() => props.data?.items || []);
const meta = computed(
  () => props.data?.meta || { page: 1, per_page: 25, total_count: 0 }
);

const fromIndex = computed(
  () => (meta.value.page - 1) * meta.value.per_page + 1
);
const toIndex = computed(() =>
  Math.min(meta.value.page * meta.value.per_page, meta.value.total_count)
);
const hasPrevious = computed(() => meta.value.page > 1);
const hasNext = computed(
  () => meta.value.page * meta.value.per_page < meta.value.total_count
);

const visibleAgents = agents => (agents || []).slice(0, 3);
</script>

<template>
  <KanbanReportWidget
    :title="t('KANBAN.REPORTS.AGING.TITLE')"
    :info-text="t('KANBAN.REPORTS.AGING.INFO')"
    :is-loading="isLoading"
    :error="error"
    :is-empty="!stepsSummary.length && !items.length"
    :empty-text="t('KANBAN.REPORTS.AGING.EMPTY')"
    @retry="emit('retry')"
  >
    <div class="flex flex-col gap-5">
      <div
        v-if="stepsSummary.length"
        class="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4"
      >
        <div
          v-for="(step, index) in stepsSummary"
          :key="step.step_id"
          class="flex flex-col gap-1 p-3 rounded-lg border border-n-slate-3 bg-n-background"
        >
          <div class="flex items-center gap-2 min-w-0">
            <span
              class="w-2 h-2 rounded-full flex-shrink-0"
              :style="{ backgroundColor: stepColor(step.color, index) }"
            />
            <span class="text-xs text-n-slate-11 truncate">{{
              step.name
            }}</span>
          </div>
          <div class="flex items-baseline gap-2">
            <span class="text-lg font-medium text-n-slate-12 tabular-nums">
              {{ formatNumber(step.wip_count, locale) }}
            </span>
            <span class="text-xs text-n-slate-10">
              {{ t('KANBAN.REPORTS.AGING.WIP') }}
            </span>
          </div>
          <div
            class="flex items-center justify-between text-xs text-n-slate-10"
          >
            <span v-tooltip="t('KANBAN.REPORTS.AGING.AVG_AGE')">
              {{ formatDuration(step.avg_age_seconds, t) }}
            </span>
            <span v-tooltip="t('KANBAN.REPORTS.AGING.MAX_AGE')">
              {{ formatDuration(step.max_age_seconds, t) }}
            </span>
          </div>
        </div>
      </div>

      <div v-if="items.length" class="flex flex-col gap-3">
        <h4 class="text-xs font-medium text-n-slate-10">
          {{ t('KANBAN.REPORTS.AGING.OLDEST_CARDS') }}
        </h4>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-xs font-medium text-n-slate-10">
                <th class="py-2 pr-4 font-medium">
                  {{ t('KANBAN.REPORTS.AGING.CARD') }}
                </th>
                <th class="py-2 px-4 font-medium">
                  {{ t('KANBAN.REPORTS.AGING.STAGE') }}
                </th>
                <th class="py-2 px-4 font-medium text-right">
                  {{ t('KANBAN.REPORTS.AGING.AGE') }}
                </th>
                <th class="py-2 px-4 font-medium text-right">
                  {{ t('KANBAN.REPORTS.AGING.VALUE') }}
                </th>
                <th class="py-2 pl-4 font-medium text-right">
                  {{ t('KANBAN.REPORTS.AGING.AGENTS') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in items"
                :key="item.task_id"
                class="border-t border-n-slate-3"
              >
                <td class="py-2 pr-4 max-w-[260px]">
                  <span class="text-n-slate-12 truncate block">
                    {{ item.title }}
                  </span>
                </td>
                <td class="py-2 px-4 text-n-slate-11 whitespace-nowrap">
                  {{ item.step_name }}
                </td>
                <td
                  class="py-2 px-4 text-right tabular-nums text-n-slate-12 whitespace-nowrap"
                >
                  {{ formatDuration(item.age_seconds, t) }}
                </td>
                <td
                  class="py-2 px-4 text-right tabular-nums text-n-slate-11 whitespace-nowrap"
                >
                  {{
                    item.value != null
                      ? formatCurrencyValue(item.value, currency, locale, {
                          compact: true,
                        })
                      : t('KANBAN.REPORTS.NONE')
                  }}
                </td>
                <td class="py-2 pl-4">
                  <div class="flex items-center justify-end -space-x-1">
                    <Avatar
                      v-for="agent in visibleAgents(item.assigned_agents)"
                      :key="agent.id"
                      v-tooltip="agent.name"
                      :src="agent.avatar_url"
                      :name="agent.name"
                      :size="20"
                      class="ring-1 ring-n-background rounded-full"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          v-if="meta.total_count > meta.per_page"
          class="flex items-center justify-between pt-1"
        >
          <span class="text-xs text-n-slate-10 tabular-nums">
            {{
              t('KANBAN.REPORTS.AGING.PAGE_INFO', {
                from: fromIndex,
                to: toIndex,
                total: meta.total_count,
              })
            }}
          </span>
          <div class="flex items-center gap-2">
            <Button
              size="xs"
              variant="faded"
              color="slate"
              icon="i-lucide-chevron-left"
              :label="t('KANBAN.REPORTS.AGING.PREVIOUS')"
              :disabled="!hasPrevious"
              @click="emit('update:page', meta.page - 1)"
            />
            <Button
              size="xs"
              variant="faded"
              color="slate"
              icon="i-lucide-chevron-right"
              trailing-icon
              :label="t('KANBAN.REPORTS.AGING.NEXT')"
              :disabled="!hasNext"
              @click="emit('update:page', meta.page + 1)"
            />
          </div>
        </div>
      </div>
    </div>
  </KanbanReportWidget>
</template>
